import omit from 'lodash/omit';
import React, { useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
// import Paper from '@mui/material/Paper';

import FormInput from './FormInput';
import config from '../../config/config';
import useDebouncedOnChange from '../../hooks/useDebouncedOnChangeV2';

const SpreadsheetCell = ({
  id,
  defaultValue,
  column, 
  onChange, 
  error,
  isLoading,
  debounceTime = 1000,
  row,
}) => {
  let _defaultValue = defaultValue || '';
  if (column.key === 'status' && ['NEW', 'CREATED_ACCOUNT', 'COMPLETED_SIGNUP', 'SCHEDULED'].includes(defaultValue)) {
    _defaultValue = 'ACTIVE';
  }
  let childProps = {
    row,
    fullWidth: true,
    type: column.type || 'text',
    defaultValue: _defaultValue,
    onChange,
    disabled: column.disabled || false,
    hideLabel: column.hideLabel || false,
    allowCreateNew: column.allowCreateNew || false,
  };
  if (column?.options) childProps.options = column.options;
  const tooltipText = (
    isLoading ? 'Syncing'
    : error ? error.message
    : ''
  );
  return (
    <TableCell 
      key={id + column.key} 
      sx={{
        minWidth: column?.minWidth, 
        border: (
          isLoading ? '1px dotted ' + config().colors.warning
          : error ? `2px dotted ${config().colors.error}` 
          : null
        ),
      }}
    >
      <Tooltip title={tooltipText}> 
        <Stack direction='row' spacing={0.5} alignItems='center' sx={{width: '100%'}}>
          {
            column?.Component
            ? <column.Component {...childProps} />
            : <FormInput {...childProps} />
          }
        </Stack>
      </Tooltip>
    </TableCell>
  );
};

const SpreadsheetRow = ({
  row,
  columns,
  errors,
  loadStates,
  onFieldChange,
}) => {
  const {data} = row;
  return (
    <TableRow key={data.id + '-row'}>
      {columns.map((col, cIdx) => (
        <SpreadsheetCell
          key={data?.id + '-' + col.key + '-cell'}
          id={data?.id}
          row={row}
          defaultValue={data?.[col.key] || ''}
          column={col}
          isLoading={loadStates?.[data.id]?.[col.key]}
          error={errors?.[row.data.id]?.[col.key]}
          onChange={(e, val) => onFieldChange(e, val, col.key, data.id)}
        />
      ))}
    </TableRow>
  );
};

const handleChange = ({
  put,
  setErrors,
  setLoadStates,
  setLastChangedId,
  errors,
  loadStates,
}) => async (e, val, fieldName, id) => {
  // console.log('Field changed:', fieldName, val || e.target.value, id);
  setLoadStates({...loadStates, [id]: {[fieldName]: true}});
  setLastChangedId(id);
  const _value = val || e?.target?.value;
  const request = await put({id, body: {[fieldName]: _value}});
  if (request.error) {
    setErrors({
      ...errors,
      [id]: {
        ...(errors?.[id] || {}),
        [fieldName]: (
          new Error(request?.data?.errors?.[0] || 'Error Syncing Update')
        ),
      },
    });
  } else if (errors?.[id]?.[fieldName]) {
    setErrors({
      ...errors,
      [id]: omit(errors, `[${id}].${fieldName}`),
    });
  }
  setLoadStates(omit(loadStates, id));
};

const handleFieldChange = ({
  errors,
  loadStates,
  put,
  setErrors, 
  setLoadStates,
  onChange,
}) => async (e, val, fieldName, id) => {
  // console.log('Field changed handler:', fieldName, id, val || e.target.value);
  const response = await onChange(e, val, fieldName, id);
  return response;
};

const SpreadSheet = ({
  rows, 
  put,
  columns = [],
  debounceTime = 500,
}) => {
  const [errors, setErrors] = useState({});
  const [lastChangedId, setLastChangedId] = useState(null);
  const [loadStates, setLoadStates] = useState({});
  const onChange = handleChange({
    put,
    setErrors,
    setLoadStates, 
    setLastChangedId,
    errors, 
    loadStates,
  });
  const debouncedOnChange = useDebouncedOnChange(onChange, debounceTime);
  const onFieldChange = handleFieldChange({
    onChange: debouncedOnChange,
  });
  return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell key={idx} sx={{minWidth: col?.minWidth}}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rIdx) => (
              <SpreadsheetRow
                key={row.data.id + '-row'}
                row={row}
                columns={columns}
                errors={errors?.[row.data.id]}
                isLoading={loadStates?.[row.data.id]}
                onFieldChange={onFieldChange}
              />
            ))}
          </TableBody>
        </Table>
    </TableContainer>
  );
};

export default SpreadSheet;